import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";
import { fmtDateTime } from "@/utils/date";

/**
 * Task-level comments thread. Any co-owner or manager can post; every other
 * assignee (plus the task creator) gets an in-app notification. Kept simple —
 * flat list, oldest → newest.
 */
export default function TaskCommentsDialog({ task, open, onOpenChange, onCommentAdded }) {
  const [comments, setComments] = useState(task?.comments || []);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => { setComments(task?.comments || []); }, [task]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = text.trim();
    if (!trimmed) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/comments`, { text: trimmed });
      setComments(data.comments || []);
      setText("");
      toast.success("Comment posted");
      onCommentAdded?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-lg" data-testid={`task-comments-dialog-${task?.id || "unknown"}`}>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <MessageSquare size={16} className="text-[#E11D2E]" /> Comments — {task?.title}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Every co-owner is notified when you post a comment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-80 overflow-y-auto scrollbar-thin pr-1" data-testid="task-comments-list">
          {comments.length === 0 ? (
            <div className="text-sm text-white/40 italic border border-dashed border-white/10 rounded-md p-4 text-center">
              No comments yet. Start the conversation.
            </div>
          ) : (
            comments.map((c) => (
              <div key={c.id} data-testid={`task-comment-${c.id}`} className="border border-white/8 rounded-md p-3 bg-black/30">
                <div className="flex justify-between text-xs text-white/50 font-mono mb-1.5">
                  <span className="text-white/70">{c.by_name}</span>
                  <span>{fmtDateTime(c.at)}</span>
                </div>
                <div className="text-sm text-white/85 whitespace-pre-wrap break-words">{c.text}</div>
              </div>
            ))
          )}
        </div>

        <form onSubmit={submit} className="flex items-end gap-2 mt-2">
          <textarea
            data-testid="task-comment-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
            }}
            placeholder="Write a comment… (Ctrl/⌘+Enter to send)"
            className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <Button
            type="submit"
            disabled={posting || !text.trim()}
            data-testid="task-comment-submit"
            className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full"
          >
            <Send size={13} className="mr-1" /> Post
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
